import { LayoutGrid } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import type { ActiveLocation } from '../model/active-location';
import type { ResolvedModule } from '../model/resolve-navigation';

/**
 * The phone's five-entry bottom bar (the standard: "sidebar becomes a five-entry bottom bar"),
 * shown by `shell.css` below 620px of shell width and absent above it.
 *
 * FOUR MODULES, THEN "ALTRO", ALWAYS (Brand Kit § The frame: "a 68 px bottom bar of five entries,
 * whose fifth opens the whole menu"). The first four modules appear and the fifth entry opens the
 * drawer holding the complete two-level rail, the display preferences and the footer. It used to
 * appear only from six modules; it is now unconditional because the drawer carries more than the
 * modules — a phone has no other way to reach the footer, the theme or the language, and the
 * header's burger gives way to the symbol there. Nothing is ever silently dropped: a module that
 * does not fit in four is one tap away in the drawer, and a bar that simply truncated would make it
 * unreachable on a phone.
 *
 * A module's TERRITORY decides which tab is current, not its landing path, so a study detail three
 * levels deep still marks `Studi clinici` — the same `ActiveLocation` the rail and the breadcrumb
 * read.
 *
 * Each entry keeps its glyph and its label. The standard's compressed-navigation rule — "every
 * entry keeps a distinguishing mark plus `title`/`aria-label`. A column of identical dots is not
 * navigation" — is about the rail, and it applies here for the same reason.
 *
 * ITS LANDMARK IS NAMED DIFFERENTLY FROM THE RAIL'S, even though it carries the same entries. The
 * two are mutually exclusive on screen — each is `display: none` in the other's band — so a real
 * user never meets both. But two navigation landmarks sharing one accessible name is the kind of
 * latent defect that becomes real the moment a width band changes, and `Navigazione rapida` is an
 * honest description of a five-entry bar rather than a label invented to dodge a duplicate.
 */
const MODULE_ENTRIES = 4;

export function BottomTabBar({
  modules,
  active,
  drawerOpen,
  onOpenDrawer,
}: {
  modules: readonly ResolvedModule[];
  active: ActiveLocation;
  drawerOpen: boolean;
  onOpenDrawer: () => void;
}) {
  const { t } = useTranslation('shell');
  const shown = modules.slice(0, MODULE_ENTRIES);
  // "Altro" is current whenever the module you are in is one of the ones it holds — otherwise a
  // phone user in the fifth module would see no tab marked at all.
  const activeIsHidden =
    active.module !== undefined && !shown.some((m) => m.id === active.module?.id);

  return (
    <nav className="shell-tabbar" aria-label={t('nav.quickBar')}>
      {shown.map((module) => (
        <Link
          key={module.id}
          to={module.path}
          className="shell-tab"
          aria-current={active.module?.id === module.id ? 'page' : undefined}
          // The tab's label IS its visible text here, so a name is only needed when there is a
          // counter to speak — the badge is a dot-sized figure that no screen reader would
          // otherwise associate with this tab.
          {...(module.count === undefined
            ? {}
            : { 'aria-label': `${module.label}, ${module.count.label}` })}
        >
          <span className="shell-tab-glyph" aria-hidden="true">
            <module.icon />
            {module.count === undefined ? null : (
              <span className="shell-tab-count">{module.count.value}</span>
            )}
          </span>
          <span className="shell-tab-label">{module.label}</span>
        </Link>
      ))}
      <button
        type="button"
        className="shell-tab"
        onClick={onOpenDrawer}
        aria-expanded={drawerOpen}
        aria-controls="app-nav-drawer"
        aria-current={activeIsHidden ? 'page' : undefined}
      >
        <span className="shell-tab-glyph" aria-hidden="true">
          <LayoutGrid />
        </span>
        <span className="shell-tab-label">{t('nav.more')}</span>
      </button>
    </nav>
  );
}
